import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { EmptyObject } from 'apollo-angular/types';
import equal from 'fast-deep-equal';
import { Observable, firstValueFrom, from, of } from 'rxjs';
import { catchError, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { AuthTokenService } from 'src/app/shared/services/auth-token.service';
import { DeviceService } from 'src/app/shared/services/device.service';
import { environment } from 'src/environments/environment';

type Headers = { [key: string]: string | string[] };
type HttpParamsType =
  | HttpParams
  | {
      [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
    };
type ResponseType = 'arraybuffer' | 'json' | 'text';
type ObserveType = 'response' | 'body';
type OptionsType = {
  headers?: HttpHeaders;
  observe?: any;
  params?: HttpParamsType;
  reportProgress?: boolean;
  responseType: any;
  withCredentials?: boolean;
};

/**
 * The purpose of this interface is to set baseurl, path, parameter and header in post, patch and get request
 * Also create headers to set api key and set authorization header if required.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiInterfaceService {
  previousPostRequestData: { path: string; body: string }; // This is used to cache the request data going out in post API calls

  previousPostRequestResult: any; // stores the result of the cached request

  // store device type here to send with http headers
  deviceType = this.deviceService.getDeviceType();

  constructor(
    private http: HttpClient,
    private apollo: Apollo,
    private deviceService: DeviceService,
    private authTokenService: AuthTokenService,
  ) {}

  /**
   * Sends a HTTP POST to the API at the given end point path.
   *
   * @deprecated Please use post$ method returning Observable.
   *
   * @param path - The end point path to send the request to.
   * @param body - The body of the post request.
   * @param requireAuth - If the call should have the auth headers attached.
   * @param params - Optional query params for the request.
   * @param headers - Custom headers object with key-value pair.
   * @param isReturningArrayBuffer - Set response type flag for file type.
   * @param fullResponse - To get response object and not the body content.
   */
  async post(
    path: string,
    body: any,
    requireAuth = false,
    params?: HttpParamsType,
    headers?: Headers,
    isReturningArrayBuffer = false,
    fullResponse = false,
  ): Promise<Observable<any>> {
    if (isReturningArrayBuffer) {
      return Promise.resolve(
        this.post$(path, body, requireAuth, params, headers, 'arraybuffer', fullResponse ? 'response' : 'body'),
      );
    }
    return Promise.resolve(
      this.post$(path, body, requireAuth, params, headers, 'json', fullResponse ? 'response' : 'body'),
    );
  }

  /**
   * Sends a HTTP POST to the API at the given end point path.
   *
   * @param path - The end point path to send the request to.
   * @param body - The body of the post request.
   * @param requireAuth - If the call should have the auth headers attached.
   * @param params - Optional query params for the request.
   * @param headers - Custom headers object with key-value pair.
   * @param responseType - Set response type.
   * @param observe - determines the return type, according to what you are interested in observing.
   */
  // prettier-ignore
  post$<T>(path: string, body: any, requireAuth?: boolean, params?: HttpParamsType, headers?: Headers): Observable<T>

  // prettier-ignore
  post$(path: string, body: any, requireAuth: boolean, params: HttpParamsType, headers: Headers, responseType: 'arraybuffer', observe?: ObserveType): Observable<ArrayBuffer>

  // prettier-ignore
  post$<T>(path: string, body: any, requireAuth: boolean, params: HttpParamsType, headers: Headers, responseType: 'json', observe?: ObserveType): Observable<T>

  // prettier-ignore
  post$(path: string, body: any, requireAuth: boolean, params: HttpParamsType, headers: Headers, responseType: 'text', observe?: ObserveType): Observable<string>

  // prettier-ignore
  post$<T = Object>(path: string, body: any, requireAuth = false, params?: HttpParamsType, headers?: Headers, responseType: ResponseType = 'json', observe: ObserveType = 'body'): Observable<T> {
    return this.generateHeaders$(requireAuth, headers).pipe(
      switchMap((headers) => {
        const options: OptionsType = {
          params,
          headers,
          responseType,
          observe,
        };

        const currentPostRequestData = { path, body };

        // Compare previous and current post request data
        if (equal(this.previousPostRequestData, currentPostRequestData) && this.previousPostRequestResult?.ok) {
          // The previous and current post data is same so we block the request going to servers if it was previously successful

          // we'll also return the result of the previous request if it was a success, if it errored, we'll let it pass
          // Since whatever is consuming this function is expecting to be returned a
          // call from the http client, we'll have to wrap it in of() to return an observable
          return of(this.previousPostRequestResult);
        }

        // The previous and current data is different so lets proceed with the request and take the data in global variable
        this.previousPostRequestData = currentPostRequestData;

        return this.http.post<T>(environment.apiUrl() + path, body, options).pipe(
          tap((res) => {
            // Tap into the response, so we can set the previousRequestPostResult to give to any subsequent duplicate API calls
            this.previousPostRequestResult = res;
          }),
          catchError((error) => {
            // if the result is an error, then we don't want to adhere to the 10 second rule, let them retry again if they choose to
            this.previousPostRequestData = null;
            this.previousPostRequestResult = null;
            console.log(error);
            throw error;
          }),
          finalize(() => {
            // finalize is called whenever the observable is closed
            setTimeout(() => {
              // To substantially block duplicate requests, we need timeout
              this.previousPostRequestData = null;
              this.previousPostRequestResult = null;
            }, 10000); // This means two post requests with same data wont be entertained within 10 seconds.
          }),
        );
      })
    );
  }

  /**
   * Sends a HTTP PUT to the API at the given end point path.
   *
   * @deprecated Please use put$ method returning Observable.
   *
   * @param path - The end point path to send the request to.
   * @param body - The body of the put request.
   * @param requireAuth - If the call should have the auth headers attached.
   * @param params - Optional query params for the request.
   * @param headers - Custom headers object with key-value pair.
   * @param isReturningArrayBuffer - Set response type flag for file type.
   * @param fullResponse - To get response object and not the body content.
   */
  async put(
    path: string,
    body: any,
    requireAuth = false,
    params?: HttpParamsType,
    headers?: Headers,
    isReturningArrayBuffer = false,
    fullResponse = false,
  ): Promise<Observable<any>> {
    if (isReturningArrayBuffer) {
      return Promise.resolve(
        this.put$(path, body, requireAuth, params, headers, 'arraybuffer', fullResponse ? 'response' : 'body'),
      );
    }
    return Promise.resolve(
      this.put$(path, body, requireAuth, params, headers, 'json', fullResponse ? 'response' : 'body'),
    );
  }

  /**
   * Sends a HTTP PUT to the API at the given end point path.
   *
   * @param path - The end point path to send the request to.
   * @param body - The body of the put request.
   * @param requireAuth - If the call should have the auth headers attached.
   * @param params - Optional query params for the request.
   * @param headers - Custom headers object with key-value pair.
   * @param responseType - Set response type.
   * @param fullResponse - To get response object and not the body content.
   */
  // prettier-ignore
  put$<T = Object>(path: string, body: any, requireAuth?: boolean, params?: HttpParamsType, headers?: Headers): Observable<T>

  // prettier-ignore
  put$(path: string, body: any, requireAuth: boolean, params: HttpParamsType, headers: Headers, responseType: 'arraybuffer', observe?: ObserveType): Observable<ArrayBuffer>

  // prettier-ignore
  put$<T = Object>(path: string, body: any, requireAuth: boolean, params: HttpParamsType, headers: Headers, responseType: 'json', observe?: ObserveType): Observable<T>

  // prettier-ignore
  put$(path: string, body: any, requireAuth: boolean, params: HttpParamsType, headers: Headers, responseType: 'text', observe?: ObserveType): Observable<string>

  // prettier-ignore
  put$<T = Object>(path: string, body: any, requireAuth = false, params?: HttpParamsType, headers?: Headers, responseType: ResponseType = 'json', observe: ObserveType = 'body'): Observable<T | ArrayBuffer> {
    return this.generateHeaders$(requireAuth, headers).pipe(
      switchMap((headers) => {
        const options: OptionsType = {
          params,
          headers,
          responseType,
          observe,
        };
        return this.http.put<T>(environment.apiUrl() + path, body, options);
      })
    );
  }

  /**
   * Sends a HTTP PATCH to the API at the given end point path.
   *
   * @deprecated Please use patch$ method returning Observable.
   *
   * @param path - The end point path to send the request to.
   * @param body - The body of the patch request.
   * @param requireAuth - If the call should have the auth headers attached.
   * @param params - Optional query params for the request.
   */
  async patch(path: string, body: any, requireAuth = false, params?: HttpParamsType): Promise<Observable<any>> {
    return Promise.resolve(this.patch$(path, body, requireAuth, params));
  }

  /**
   * Sends a HTTP PATCH to the API at the given end point path.
   *
   * @param path - The end point path to send the request to.
   * @param body - The body of the patch request.
   * @param requireAuth - If the call should have the auth headers attached.
   * @param params - Optional query params for the request.
   */
  patch$<T>(path: string, body: any, requireAuth = false, params?: HttpParamsType): Observable<T> {
    return this.generateHeaders$(requireAuth).pipe(
      switchMap((headers) => this.http.patch<T>(environment.apiUrl() + path, body, { params, headers })),
    );
  }

  /**
   * Sends a HTTP GET to the API at the given end point path.
   *
   * @deprecated Please use get$ method returning Observable.
   *
   * @param path - The end point path to send the request to.
   * @param requireAuth - If the call should have the auth headers attached.
   * @param params - Optional query params for the request.
   * @param withCredentials - Optional if the request should attach http cookie for auth.
   * @param isReturningArrayBuffer -Set response type flag for file type.
   */
  async get(
    path: string,
    requireAuth = false,
    params?: HttpParamsType,
    withCredentials?: boolean,
    isReturningArrayBuffer = false,
  ): Promise<Observable<any>> {
    if (isReturningArrayBuffer) {
      return Promise.resolve(this.get$(path, requireAuth, params, withCredentials, 'arraybuffer'));
    }
    return Promise.resolve(this.get$(path, requireAuth, params, withCredentials, 'json'));
  }

  /**
   * Sends a HTTP GET to the API at the given end point path.
   *
   * @param path - The end point path to send the request to.
   * @param requireAuth - If the call should have the auth headers attached.
   * @param params - Optional query params for the request.
   * @param withCredentials - Optional if the request should attach http cookie for auth.
   * @param responseType - Set response type.
   */
  // prettier-ignore
  get$<T = Object>(path: string, requireAuth?: boolean, params?: HttpParamsType, withCredentials?: boolean): Observable<T>

  // prettier-ignore
  get$(path: string, requireAuth: boolean, params: HttpParamsType, withCredentials: boolean, responseType: 'arraybuffer'): Observable<ArrayBuffer>

  // prettier-ignore
  get$<T = Object>(path: string, requireAuth: boolean, params: HttpParamsType, withCredentials: boolean, responseType: 'json'): Observable<T>

  // prettier-ignore
  get$(path: string, requireAuth: boolean, params: HttpParamsType, withCredentials: boolean, responseType: 'text'): Observable<string>

  // prettier-ignore
  get$<T = Object>(path: string, requireAuth = false, params?: HttpParamsType, withCredentials?: boolean, responseType: ResponseType = 'json'): Observable<T> {
    return this.generateHeaders$(requireAuth).pipe(
      switchMap((headers) => {
        const options: OptionsType = {
          params,
          headers,
          responseType,
          withCredentials,
        };
        return this.http.get<T>(environment.apiUrl() + path, options);
      })
    );
  }

  /**
   * Sends a HTTP DELETE to the API at the given end point path.
   *
   * @deprecated Please use delete$ method returning Observable.
   *
   * @param path - The end point path to send the request to.
   * @param requireAuth - If the call should have the auth headers attached.
   * @param params - Optional query params for the request.
   * @param withCredentials - Optional if the request should attach http cookie for auth.
   */
  async delete(
    path: string,
    requireAuth = false,
    params?: HttpParamsType,
    withCredentials?: boolean,
  ): Promise<Observable<any>> {
    return Promise.resolve(this.delete$(path, requireAuth, params, withCredentials));
  }

  /**
   * Sends a HTTP DELETE to the API at the given end point path.
   *
   * @param path - The end point path to send the request to.
   * @param requireAuth - If the call should have the auth headers attached.
   * @param params - Optional query params for the request.
   * @param withCredentials - Optional if the request should attach http cookie for auth.
   */
  delete$(path: string, requireAuth = false, params?: HttpParamsType, withCredentials?: boolean): Observable<Object> {
    return this.generateHeaders$(requireAuth).pipe(
      switchMap((headers) => this.http.delete(environment.apiUrl() + path, { params, headers, withCredentials })),
    );
  }

  /**
   * Sends a graphQL query.
   *
   * @param query - The gql query (a query string wrapped in gql())..
   * @param variables - The gql variables.
   * @returns - Observable.
   */
  gqlQuery$<T, V = EmptyObject>(query, variables?): Observable<T> {
    return this.apollo
      .query<T, V>({
        query,
        variables,
      })
      .pipe(
        take(1),
        map((result) => result.data),
      );
  }

  /**
   * Sends a graphQL mutation.
   *
   * @param mutation - The gql mutation (a mutation string wrapped in gql()).
   * @param variables - Variables that should be injected into the mutation.
   * @returns - Observable.
   */
  gqlMutation$<T, V = EmptyObject>(mutation, variables?): Observable<T> {
    return this.apollo
      .mutate<T, V>({
        mutation,
        variables,
      })
      .pipe(
        take(1),
        map((result) => result.data),
      );
  }

  /**
   * Sends a graphQL query.
   *
   * @param query - The gql query (a query string wrapped in gql())..
   * @param variables - Variables that should be injected into the mutation.
   */
  async gqlQuery<T, V = EmptyObject>(query, variables?): Promise<T> {
    return await firstValueFrom(
      this.apollo
        .query<T, V>({
          query,
          variables,
        })
        .pipe(
          map((result) => result.data),
          catchError((error) => {
            // This is required for backwards compatibility with the way
            // that error handling has been originally implemented.
            throw error.graphQLErrors;
          }),
        ),
    );
  }

  /**
   * Sends a graphQL mutation.
   *
   * @param mutation - The gql mutation (a mutation string wrapped in gql()).
   * @param variables - Variables that should be injected into the mutation.
   */
  async gqlMutation<T, V = EmptyObject>(mutation, variables?): Promise<T> {
    return await firstValueFrom(
      this.apollo
        .mutate<T, V>({
          mutation,
          variables,
        })
        .pipe(map((result) => result.data)),
    );
  }

  /**
   * Add Authorization Token and API key to the request.
   *
   * @param authHeaderRequired - If the call should have the auth
   * @param headers - Custom headers object with key-value pair.
   * headers attached.
   */
  generateHeaders$(authHeaderRequired: boolean, headers?: Headers): Observable<HttpHeaders> {
    // add our api key
    let header = new HttpHeaders({ 'X-API-KEY': environment.apiKey });

    // add the isWeb header (used for analytics)
    header = header.set('isWeb', 'false');

    // add device type
    header = header.set('deviceType', this.deviceType);

    // add any other headers required by the environment config
    for (const headerKey in environment.apiHeaders) {
      header = header.set(headerKey, environment.apiHeaders[headerKey]);
    }

    // add any other custom headers.
    if (headers) {
      for (const headerKey in headers) {
        header = header.set(headerKey, headers[headerKey]);
      }
    }

    if (authHeaderRequired) {
      const accessToken = this.authTokenService.accessToken;

      if (accessToken) {
        header = header.set('Authorization', `Bearer ${accessToken}`);
      }
    }

    return from(this.authTokenService.getTargetUUID()).pipe(
      map((targetUUID) => {
        if (targetUUID) {
          header = header.set('Target-User-Uuid', targetUUID);
        }
        return header;
      }),
    );
  }
}
